import React, { useState, useEffect, useMemo } from "react";
import { useHomeLogic } from "../hooks/useHomeLogic";
import { useUser } from "../context/user";
import { useAppData } from "../context/data";
import { getAvatarUrl } from "../hooks/useAvatarUtils";
import { Search, Bell, Check, X } from "lucide-react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { getOrCreateChat } from "../services/meetlyService";
import { getCurrentUserId } from "../services/userService";
import Events from "./Events";
import MapScreen from "./MapScreen";
import CommunitiesScreen from "./Communities";
import CommunityDetailScreen from "./CommunityDetail";
import ChatScreen from "./Chat";
import ProfileScreen from "./Profile";
import AttendeeProfile from "./AttendeeProfile";
import HomeScreen from "./HomeScreen";
import {
  MainLayout,
  LoaderSpinner,
  CreateEventModal,
  CreateCommunityModal,
  BottomNavBar,
} from "./UI";

import styles from "./Home.module.css";

/**
 * Home component.
 * Acts as the master container shell and tab navigator for authenticated users,
 * delegating business logic, state management, and personalized recommendations
 * to the useHomeLogic custom hook. Features a minimal global persistent top header
 * with integrated real-time notifications.
 */
export default function Home({
  loading = false,
  onLogout,
  onCreateEvent,
  onCreateCommunity,
  onAddMatch,
  onToggleJoinEvent,
}) {
  const { userData, setUserData } = useUser();
  const { events, communities, matches } = useAppData();

  // Extract state, handlers, and personalized recommendation data from the custom hook
  const {
    activeTab,
    setActiveTab,
    selectedEvent,
    setSelectedEvent,
    viewingEventDetails,
    setViewingEventDetails,
    selectedProfileCommunity,
    setSelectedProfileCommunity,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    isCreatingEvent,
    setIsCreatingEvent,
    isCreatingCommunity,
    setIsCreatingCommunity,
    handleTabChange,
    safeEvents,
    safeMatches,
    safeCommunities,
    filteredEvents,
    filteredCommunities,
    featuredEvent,
    firstRowEvents,
    secondRowEvents,
    thirdRowEvents,
    allEvents,
    firstRowTitle,
    secondRowTitle,
    myCreatedEvents,
  } = useHomeLogic(events, communities, matches, userData);

  const joinedEvents = userData?.joinedEvents || [];

  // Notifications & Invitations State
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // State for viewing another user's profile from connections or chat
  const [viewingUserProfile, setViewingUserProfile] = useState(null);

  // Listen for this user's pending invitations in real time globally
  useEffect(() => {
    const myUid = getCurrentUserId(userData);
    if (!myUid) return;

    const q = query(
      collection(db, "invitations"),
      where("receiverId", "==", myUid),
      where("status", "==", "pending")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invs = [];
      snapshot.forEach((docSnap) => {
        invs.push({ id: docSnap.id, ...docSnap.data() });
      });
      setPendingInvitations(invs);
    });

    return () => unsubscribe();
  }, [userData]);

  // Filter invitations to prevent duplicate entries in the UI
  const uniqueInvitations = useMemo(() => {
    const map = new Map();
    pendingInvitations.forEach((inv) => {
      const uniqueKey = inv.eventId || inv.id;
      if (!map.has(uniqueKey)) {
        map.set(uniqueKey, inv);
      }
    });
    return Array.from(map.values());
  }, [pendingInvitations]);

  // Handle an invitation response (Accept / Decline) globally
  const handleInviteResponse = async (invitation, accept) => {
    const invRef = doc(db, "invitations", invitation.id);
    const myUid = getCurrentUserId(userData);

    // Instant optimistic update in local state to remove invitation immediately
    setPendingInvitations((prev) =>
      prev.filter((inv) => inv.id !== invitation.id)
    );

    try {
      if (!accept) {
        await updateDoc(invRef, { status: "rejected" });
        return;
      }

      // Mark the invitation as accepted in Firestore
      await updateDoc(invRef, { status: "accepted" });

      // Automatically join the event if linked and not already joined
      if (invitation.eventId && onToggleJoinEvent) {
        if (!joinedEvents.includes(invitation.eventId)) {
          onToggleJoinEvent(invitation.eventId);
        }
      }

      // Find-or-create the chat between these two users
      await getOrCreateChat(
        myUid,
        invitation.senderId,
        `Accepted invitation to ${invitation.eventTitle}`
      );

      // Switch to the chat tab
      handleTabChange("chat");
      setShowNotifications(false);
    } catch (error) {
      console.error("Error processing invitation response:", error);
    }
  };

  /**
   * Handles chat match persistence by forwarding the request to the parent handler.
   */
  const handleAddMatch = async (newMatch) => {
    if (onAddMatch) {
      onAddMatch(newMatch);
    }
  };

  return (
    <MainLayout>
      <div className={styles.mainScrollArea}>
        {viewingEventDetails ? (
          // Event details sub-view component
          <Events
            event={viewingEventDetails}
            onBack={() => setViewingEventDetails(null)}
            isJoined={joinedEvents.includes(viewingEventDetails.id)}
            onJoin={() => {
              if (onToggleJoinEvent && viewingEventDetails?.id) {
                onToggleJoinEvent(viewingEventDetails.id);
              }
            }}
            onOpenMap={(ev) => {
              if (ev) setSelectedEvent(ev);
              setViewingEventDetails(null);
              setActiveTab("map");
            }}
            onAddMatch={handleAddMatch}
            userInterests={userData?.interests || []}
            onSelectEvent={(ev) => setViewingEventDetails(ev)}
          />
        ) : (
          <>
            {loading ? (
              <LoaderSpinner />
            ) : (
              <>
                {/*GLOBAL TOP HEADER WITH SEARCH AND NOTIFICATION BELL*/}
                <div className={`${styles.topHeader} ${styles.mb6}`}>
                  <div
                    onClick={() => {
                      setViewingUserProfile(null);
                      handleTabChange("profile");
                    }}
                    className={styles.userAvatarWrapper}
                    title="View Profile"
                  >
                    <img
                      src={getAvatarUrl(userData)}
                      alt="User Avatar"
                      className={styles.avatarImg}
                    />
                  </div>

                  <div className={styles.searchBarContainer}>
                    <Search size={18} className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder={
                        activeTab === "communities"
                          ? "Look for communities..."
                          : activeTab === "chat"
                          ? "Look for chats..."
                          : activeTab === "map"
                          ? "Look for locations..."
                          : "Look for activities..."
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>

                  {/* Notifications Bell Dropdown */}
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      className={styles.filterBtn}
                      title="Notifications"
                      style={{ position: "relative" }}
                    >
                      <Bell size={18} />
                      {uniqueInvitations.length > 0 && (
                        <span className={styles.notificationBadge}>
                          {uniqueInvitations.length}
                        </span>
                      )}
                    </button>

                    {showNotifications && (
                      <div className={styles.notificationDropdown}>
                        <div className={styles.dropdownHeader}>
                          <h4>Invitations</h4>
                          <span>{uniqueInvitations.length} new</span>
                        </div>

                        {uniqueInvitations.length === 0 ? (
                          <p className={styles.emptyNotifications}>
                            No pending invitations
                          </p>
                        ) : (
                          <div className={styles.inviteList}>
                            {uniqueInvitations.map((inv) => (
                              <div key={inv.id} className={styles.inviteItem}>
                                <div className={styles.inviteSenderRow}>
                                  {inv.senderAvatar && (
                                    <img
                                      src={inv.senderAvatar}
                                      alt={inv.senderName}
                                      className={styles.inviteAvatar}
                                    />
                                  )}
                                  <div>
                                    <p className={styles.inviteSenderName}>
                                      {inv.senderName}
                                    </p>
                                    <p className={styles.inviteSubtitle}>
                                      Invited you to <b>{inv.eventTitle}</b>
                                    </p>
                                  </div>
                                </div>

                                <div className={styles.inviteActionsRow}>
                                  <button
                                    onClick={() =>
                                      handleInviteResponse(inv, true)
                                    }
                                    className={styles.inviteAcceptBtn}
                                  >
                                    <Check size={14} /> Accept
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleInviteResponse(inv, false)
                                    }
                                    className={styles.inviteDeclineBtn}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/*HOME TAB (Dashboard with personalized rows)*/}
                {activeTab === "home" && (
                  <HomeScreen
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    featuredEvent={featuredEvent}
                    filteredEvents={filteredEvents}
                    filteredCommunities={filteredCommunities}
                    onSelectCommunity={(comm) =>
                      setSelectedProfileCommunity(comm)
                    }
                    firstRowEvents={firstRowEvents}
                    secondRowEvents={secondRowEvents}
                    thirdRowEvents={thirdRowEvents}
                    allEvents={allEvents}
                    firstRowTitle={firstRowTitle}
                    secondRowTitle={secondRowTitle}
                    thirdRowTitle="More suggestions"
                    allEventsTitle="All Events"
                    onSelectEvent={(ev) => setViewingEventDetails(ev)}
                    onOpenCreateEvent={() => setIsCreatingEvent(true)}
                    onNavigateProfile={() => {
                      setViewingUserProfile(null);
                      handleTabChange("profile");
                    }}
                    onToggleJoinEvent={onToggleJoinEvent}
                    joinedEvents={joinedEvents}
                    hideHeader={true}
                  />
                )}

                {/*MAP TAB*/}
                {activeTab === "map" && (
                  <MapScreen
                    events={safeEvents}
                    selectedEvent={selectedEvent}
                    onSelectEvent={(e) => setSelectedEvent(e)}
                    onViewDetails={(e) => setViewingEventDetails(e)}
                    searchQuery={searchQuery}
                  />
                )}

                {/*COMMUNITIES TAB*/}
                {activeTab === "communities" && (
                  <div className={styles.stackSpace6}>
                    <CommunitiesScreen
                      communities={safeCommunities}
                      onCreateCommunity={onCreateCommunity}
                      searchQuery={searchQuery}
                    />
                  </div>
                )}

                {/*CHAT TAB*/}
                {activeTab === "chat" && (
                  <ChatScreen
                    matches={safeMatches}
                    searchQuery={searchQuery}
                    onViewProfile={(chatUser) =>
                      setViewingUserProfile(chatUser)
                    }
                  />
                )}

                {/*PROFILE TAB*/}
                {activeTab === "profile" &&
                  (selectedProfileCommunity ? (
                    <CommunityDetailScreen
                      community={selectedProfileCommunity}
                      onBack={() => setSelectedProfileCommunity(null)}
                    />
                  ) : viewingUserProfile ? (
                    <AttendeeProfile
                      user={viewingUserProfile}
                      onBack={() => setViewingUserProfile(null)}
                      onSelectEvent={(ev) => setViewingEventDetails(ev)}
                    />
                  ) : (
                    <ProfileScreen
                      joinedEventsCount={joinedEvents.length}
                      joinedEventsList={safeEvents.filter((e) =>
                        joinedEvents.includes(e?.id)
                      )}
                      communitiesList={safeCommunities}
                      matchesList={safeMatches}
                      myCreatedEvents={myCreatedEvents}
                      onLogout={onLogout}
                      onSelectEvent={(ev) => setViewingEventDetails(ev)}
                      onSelectCommunity={(comm) =>
                        setSelectedProfileCommunity(comm)
                      }
                      onSelectConnection={(connectionUser) =>
                        setViewingUserProfile(connectionUser)
                      }
                      setIsCreatingEvent={setIsCreatingEvent}
                      setIsCreatingCommunity={setIsCreatingCommunity}
                      onBackToHome={() => handleTabChange("home")}
                    />
                  ))}
              </>
            )}
          </>
        )}

        {/* Modal for creating a new event */}
        <CreateEventModal
          isOpen={isCreatingEvent}
          onClose={() => setIsCreatingEvent(false)}
          onCreateEvent={(newEvent) => {
            const eventWithCreator = {
              ...newEvent,
              createdBy: userData?.uid || userData?.id,
            };
            if (onCreateEvent) onCreateEvent(eventWithCreator);
            setIsCreatingEvent(false);
          }}
        />

        {/* Modal for creating a new community */}
        <CreateCommunityModal
          isOpen={isCreatingCommunity}
          onClose={() => setIsCreatingCommunity(false)}
          onCreateCommunity={(newCommunity) => {
            if (onCreateCommunity) onCreateCommunity(newCommunity);
            setIsCreatingCommunity(false);
          }}
        />
      </div>

      <BottomNavBar
        activeTab={activeTab}
        selectedEvent={viewingEventDetails || selectedEvent}
        handleTabChange={(tab) => {
          setViewingUserProfile(null);
          handleTabChange(tab);
        }}
        matchesCount={safeMatches.length}
      />
    </MainLayout>
  );
}
